import type { PaginationProps } from '@components/shared/Table/components/Pagination/types'
import type {
  GetLastDistributionValuesRequestProps,
  GetOtherServicesRequestProps,
  GetServiceDescriptionsRequestProps,
  GetServiceGroupingRequestProps,
  RemoveOtherServiceRequestProps,
  RemoveSelectedServicesRequestProps,
  RemoveServiceRequestProps,
  ResetClientRequestProps
} from '@services/api/invoicing/clients/types'
import type { GetWeeklyBreakdownRequestProps } from '@services/api/invoicing/types'
import type { DistributeBillingModalFormProps } from '../components/DistributeBillingModal/form/types'
import type { AddEditServiceModalFormProps } from '../components/AddEditServiceModal/form/types'
import type { AddEditOtherServicesModalFormProps } from '@pages/Invoicing/components/AddEditOtherServicesModal/form/types'

export const WEEKDAY_MAP: Record<string, string> = {
  seg: 'segunda-feira',
  ter: 'terça-feira',
  qua: 'quarta-feira',
  qui: 'quinta-feira',
  sex: 'sexta-feira',
  sab: 'sábado',
  dom: 'domingo'
}

export interface OtherServicesItemProps {
  clientName: string
  billingReferenceDate: string
  billingStartDate: string
  billingTaxPercentage: number
  branchCode: string
  clientId: string
  clientType: string
  createdAt: string
  description: string
  grossTotalAmount: number
  id: number
  netTotalAmount: number
  observations: string
  totalQuantity: number
  unitGrossValue: number
  updatedAt: string
}

export interface OtherServicesResponseProps extends PaginationProps {
  grossTotalAmount: number
  netTotalAmount: number
  items: OtherServicesItemProps[]
}

export interface ServiceDescriptionsResponseProps {
  billingTaxPercentage: number
  description: string
  id: number
  unitGrossValue: number
}

export interface ServiceGroupingResponseProps {
  description: string
}

export interface LastDistributionValuesResponseProps {
  billingReferenceDate: string
  createdAt: string
  holidays: string[]
  previousMonthBillingDays: number
  saturdayPercentage: number
  startBillingDate: string
  sundayHolidayPercentage: number
  userName: string
  weekdayPercentage: number
}

export interface DailyGrossTotalsProps {
  data: string
  valorTotal: number
}

export interface DailyNetTotalsProps {
  data: string
  valorTotal: number
}

export interface DailyTotalQuantitiesProps {
  data: string
  dia: string
  valorTotal: number
}

export interface DayValueProps {
  data: string
  dia: string
  valor: number
}

export interface WeeklyBreakdownProps {
  agrupamentoServico: string
  cdClienteNl: string
  cdItemVenda: string
  codFatAgrupado: number
  contcVlrlFatPerimposto: number
  createdAt: string
  desClienteNl: string
  desTipoCliente: string
  diaFaturamento: string
  fatDtCompetencia: string
  filiCodigo: string
  nmItemVenda: string
  periodoFim: string
  periodoInicio: string
  qtFaturadaPrevia: number
  updatedAt: string
  valorDias: DayValueProps[]
  vlBrutoUnitario: number
  vlTotalBrutoPrevia: number
  vlTotalLiquidaPrevia: number
}

export interface WeeklyBreakdownResponseProps extends PaginationProps {
  items: WeeklyBreakdownProps[]
  dailyGrossTotals: DailyGrossTotalsProps[]
  dailyNetTotals: DailyNetTotalsProps[]
  dailyTotalQuantities: DailyTotalQuantitiesProps[]
  grossTotalAmount: number
  netTotalAmount: number
  canEdit?: boolean
  canDeleteServices?: boolean
  canRestoreForecast?: boolean
}

export interface ClientModalProps {
  weeklyBreakdownData: WeeklyBreakdownResponseProps | null
  setWeeklyBreakdownData: (value: WeeklyBreakdownResponseProps | null) => void

  isWeeklyBreakdownLoading: boolean
  setIsWeeklyBreakdownLoading: (value: boolean) => void

  isDistributeBillingLoading: boolean
  setIsDistributeBillingLoading: (value: boolean) => void

  isResetClientLoading: boolean
  setIsResetClientLoading: (value: boolean) => void

  lastDistributionValuesData: LastDistributionValuesResponseProps | null
  setLastDistributionValuesData: (value: LastDistributionValuesResponseProps | null) => void

  serviceGroupingData: ServiceGroupingResponseProps[]
  setServiceGroupingData: (value: ServiceGroupingResponseProps[]) => void

  isServiceGroupingLoading: boolean
  setIsServiceGroupingLoading: (value: boolean) => void

  serviceDescriptionsData: ServiceDescriptionsResponseProps[]
  setServiceDescriptionsData: (value: ServiceDescriptionsResponseProps[]) => void

  isServiceDescriptionsLoading: boolean
  setIsServiceDescriptionsLoading: (value: boolean) => void

  isCreateNewServiceLoading: boolean
  setIsCreateNewServiceLoading: (value: boolean) => void

  isRemoveServiceLoading: boolean
  setIsRemoveServiceLoading: (value: boolean) => void

  isRemoveSelectedServicesLoading: boolean
  setIsRemoveSelectedServicesLoading: (value: boolean) => void

  isLastDistributionValuesLoading: boolean
  setIsLastDistributionValuesLoading: (value: boolean) => void

  otherServicesData: OtherServicesResponseProps | null
  setOtherServicesData: (value: OtherServicesResponseProps | null) => void

  isOtherServicesLoading: boolean
  setIsOtherServicesLoading: (value: boolean) => void

  isCreateNewOtherServiceLoading: boolean
  setIsCreateNewOtherServiceLoading: (value: boolean) => void

  isRemoveOtherServiceLoading: boolean
  setIsRemoveOtherServiceLoading: (value: boolean) => void

  getWeeklyBreakdown: (payload: GetWeeklyBreakdownRequestProps) => Promise<void>
  getOtherServices: (payload: GetOtherServicesRequestProps) => Promise<void>
  distributeBilling: (formData: DistributeBillingModalFormProps) => Promise<boolean>
  resetClient: (payload: ResetClientRequestProps) => Promise<boolean>
  getLastDistributionValues: (payload: GetLastDistributionValuesRequestProps) => Promise<void>
  getServiceGrouping: (payload: GetServiceGroupingRequestProps) => Promise<void>
  getServiceDescriptions: (payload: GetServiceDescriptionsRequestProps) => Promise<void>
  createNewService: (formData: AddEditServiceModalFormProps) => Promise<boolean>
  editService: (formData: AddEditServiceModalFormProps) => Promise<boolean>
  removeService: (payload: RemoveServiceRequestProps) => Promise<boolean>
  removeSelectedServices: (payload: RemoveSelectedServicesRequestProps) => Promise<boolean>
  createNewOtherService: (formData: AddEditOtherServicesModalFormProps) => Promise<boolean>
  editOtherService: (formData: AddEditOtherServicesModalFormProps) => Promise<boolean>
  removeOtherService: (payload: RemoveOtherServiceRequestProps) => Promise<boolean>
}
